import * as Updates from 'expo-updates'
import { useCallback, useEffect, useReducer, useRef } from 'react'
import { AppState, AppStateStatus, Platform } from 'react-native'
import * as BackgroundTask from 'expo-background-task'
import * as TaskManager from 'expo-task-manager'
import { getSegmentClient } from '../models/analytics/segment'

const isDev = __DEV__ || process.env.NODE_ENV === 'development'

const trackOtaUpdateApplied = (
  source: 'background-task' | 'foreground-check',
  result: Updates.UpdateFetchResult
) => {
  getSegmentClient()?.track('OTA Update Applied', {
    source,
    fromUpdateId: Updates.updateId ?? 'is_nullish',
    toUpdateId: result.manifest?.id ?? 'is_nullish',
    channel: Updates.channel ?? 'is_nullish',
    runtimeVersion: Updates.runtimeVersion ?? 'is_nullish',
  })
}

export const EXPO_UPDATE_BACKGROUND_TASK = 'EXPO_UPDATE_BACKGROUND_TASK'

export async function createExpoUpdateBackgroundTask() {
  if (Platform.OS !== 'web' && !isDev) {
    await BackgroundTask.registerTaskAsync(EXPO_UPDATE_BACKGROUND_TASK, {
      minimumInterval: 12 * 60 // 12 hours
    })

    TaskManager.defineTask(EXPO_UPDATE_BACKGROUND_TASK, async () => {
      const update = await Updates.checkForUpdateAsync()
      if (update.isAvailable) {
        const result = await Updates.fetchUpdateAsync()
        if (result.isNew) {
          trackOtaUpdateApplied('background-task', result)
          // No flush: short-lived background context; relying on Segment's persisted queue.
          await Updates.reloadAsync()
        }
      }
    })
  }
}

const actionKind = {
  HYDRATE: 'HYDRATE',
  ERROR: 'ERROR'
} as const
interface Action {
  type: keyof typeof actionKind
}
interface State {
  hydrated: boolean
  isPending: boolean
  isError: boolean
  // error?: unknown
}

function reducer(state: State, action: Action) {
  switch (action.type) {
    case actionKind.HYDRATE:
      return {
        ...state,
        hydrated: true,
        isPending: false,
        isError: false
      }
    case actionKind.ERROR:
      return {
        ...state,
        hydrated: true,
        isPending: false,
        isError: true
      }
    default:
      return state
  }
}

const initialState = { hydrated: false, isPending: true, isError: false }
const initialStateDev = { hydrated: true, isPending: false, isError: false }

export const useExpoUpdateStatus = () => {
  const [state, dispatch] = useReducer(reducer, isDev ? initialStateDev : initialState)
  const appState = useRef(AppState.currentState)
  const isChecking = useRef(false)

  const checkExpoUpdate = useCallback(async () => {
    // Prevent concurrent checks
    if (isChecking.current) return

    isChecking.current = true
    try {
      const update = await Updates.checkForUpdateAsync()
      if (!update.isAvailable) {
        //
        dispatch({ type: actionKind.HYDRATE })
        return
      }
      const result = await Updates.fetchUpdateAsync()

      if (!result.isNew) {
        dispatch({ type: actionKind.HYDRATE })
        return
      }
      trackOtaUpdateApplied('foreground-check', result)
      await Promise.race([
        getSegmentClient()?.flush(),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ])
      await Updates.reloadAsync()
    } catch (error) {
      dispatch({ type: actionKind.ERROR })
    } finally {
      isChecking.current = false
    }
  }, [])

  useEffect(() => {
    !isDev && checkExpoUpdate()
  }, [checkExpoUpdate])

  // Check for updates when app comes to foreground
  useEffect(() => {
    if (isDev) return

    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      // App has come to the foreground from background
      if (appState.current.match(/inactive|background/) && nextAppState === 'active') {
        checkExpoUpdate()
      }
      appState.current = nextAppState
    }

    const subscription = AppState.addEventListener('change', handleAppStateChange)

    return () => {
      subscription.remove()
    }
  }, [checkExpoUpdate])

  return state
}
